import { useMemo } from 'react';
import { useClient } from 'urql';
import { fromPromise } from 'xstate5';
import {
  type DietaryPreferencesMachineImplementations,
  mergeRemoteDietaryPreferences,
} from '@sg/shared-fe/features/menu';

import {
  GetDietaryPreferencesDocument as Document,
  type GetDietaryPreferencesQuery as Query,
  type GetDietaryPreferencesQueryVariables as Variables,
  useUpdateDietaryPreferencesMutation,
} from './graphql/CustomerDietaryPreferences.generated';
import { getDietaryPreferencesList } from './helpers';

// ─────────────────────────────────────────────────────────────────────────────

export const useFetchDietaryPreferencesActor = () => {
  const client = useClient();
  const [, updatePreferences] = useUpdateDietaryPreferencesMutation();

  return useMemo<FetchCustomerDietaryPreferences>(
    () =>
      fromPromise(async (args) => {
        const { input: existingDietaryPreferences } = args;

        const query = client.query<Query, Variables>;

        const response = await query(Document, {}, { requestPolicy: 'network-only' }).toPromise();

        const preferencesData = response.data?.getPreferences;

        // ─── Error State ─────────────────────────────

        if (!preferencesData) {
          throw new Error('Failed to fetch customer dietary preferences');
        }

        // ─── Synchronize local restrictions if needed

        if (!preferencesData?.hasSyncedRestrictions) {
          const dietaryRestrictions = getDietaryPreferencesList(existingDietaryPreferences);

          const { error } = await updatePreferences({
            input: { dietaryRestrictions },
          });

          if (error) {
            throw new Error('Failed to sync local dietary preferences to remote');
          }

          return existingDietaryPreferences;
        }

        // ─────────────────────────────────────────────

        const dietaryRestrictions = preferencesData?.dietaryRestrictions ?? [];

        return mergeRemoteDietaryPreferences(dietaryRestrictions);
      }),
    [client.query, updatePreferences],
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type FetchCustomerDietaryPreferences = NonNullable<
  NonNullable<DietaryPreferencesMachineImplementations['actors']>['fetchCustomerDietaryPreferences']
>;
